import { ApprovalStatus, WorkflowRunStatus, type ApprovalNode, type WorkflowInstanceStatus } from 'types/SPM';

// Derived here rather than on the endpoint because the monolith does not store the workflow
// instance status, which Failed, Cancelled and the dash all depend on.
const FAILED_INSTANCE_STATUSES: WorkflowInstanceStatus[] = ['failed', 'died'];
const INDETERMINATE_INSTANCE_STATUSES: WorkflowInstanceStatus[] = ['new', 'suspended'];

export const DEAD_INSTANCE_STATUSES: WorkflowInstanceStatus[] = [...FAILED_INSTANCE_STATUSES, 'canceled'];

export const isDeadInstanceStatus = (status: string | null | undefined): boolean =>
  !!status && DEAD_INSTANCE_STATUSES.some((dead) => dead === status);

export const isFailedInstanceStatus = (status: string | null | undefined): boolean =>
  !!status && FAILED_INSTANCE_STATUSES.some((failed) => failed === status);

type DeriveArgs = {
  /** Null while the plan's workflows load, or when no workflow claims this run. */
  instanceStatus: WorkflowInstanceStatus | null;
  /** Already reads Completed once the run's change group closes. */
  approvalStatus: ApprovalStatus;
  nodes: ApprovalNode[];
};

export const deriveWorkflowRunStatus = ({ instanceStatus, approvalStatus, nodes }: DeriveArgs): WorkflowRunStatus => {
  if (instanceStatus) {
    if (FAILED_INSTANCE_STATUSES.includes(instanceStatus)) return WorkflowRunStatus.FAILED;
    if (instanceStatus === 'canceled') return WorkflowRunStatus.CANCELLED;
    if (instanceStatus === 'succeeded') return WorkflowRunStatus.COMPLETED;
    if (INDETERMINATE_INSTANCE_STATUSES.includes(instanceStatus)) return WorkflowRunStatus.UNKNOWN;
  }

  if (approvalStatus === ApprovalStatus.COMPLETED) return WorkflowRunStatus.COMPLETED;
  if (nodes.length > 0 && nodes.every((node) => node.is_approved === true)) return WorkflowRunStatus.COMPLETED;

  // A node's deadline is shared, so one unresolved approver on an overdue node holds up the run.
  if (nodes.some((node) => node.is_approved === null && node.is_overdue)) return WorkflowRunStatus.PAST_DUE;

  return WorkflowRunStatus.IN_PROGRESS;
};
